/**
 * Field validation using Zod schemas. Template-agnostic; caller passes bid details for schema selection.
 */
import { createDirectProductSchema, createIndirectProductSchema } from '../../bidAnalysisValidation';

export interface ValidateFieldDeps {
  currentBidDetails: { product_type?: string | null; template_type?: string | null } | null | undefined;
  templateType: string | null | undefined;
  analysisType: string;
}

export function validateField(
  fieldName: string,
  value: any,
  deps: ValidateFieldDeps
): string | null {
  try {
    const { currentBidDetails, templateType, analysisType } = deps;
    const currentProductType = currentBidDetails?.product_type ?? null;
    const currentTemplateType =
      currentBidDetails?.template_type?.toLowerCase() || templateType?.toLowerCase() || 'mas';
    const isNC = currentTemplateType === 'nc';

    if (fieldName === 'suggestedBidPrice') {
      if (isNC) {
        return null;
      }

      const schema = createDirectProductSchema(analysisType);
      const fieldSchema = schema.shape[fieldName as keyof typeof schema.shape];

      if (fieldSchema) {
        try {
          fieldSchema.parse(value);
          return null;
        } catch (error: any) {
          let issues = error?.issues;
          if (!issues && error?.errors) {
            issues = error.errors;
          }
          if (!issues && Array.isArray(error)) {
            issues = error;
          }

          if (issues && Array.isArray(issues) && issues.length > 0) {
            const firstIssue = issues[0];
            let errorMessage = firstIssue?.message;

            if (errorMessage) {
              if (
                errorMessage === 'BID_PRICE_VA_VALIDATION_FAILED' &&
                fieldName === 'suggestedBidPrice'
              ) {
                const currentPrice =
                  value !== null && value !== undefined
                    ? typeof value === 'number'
                      ? value
                      : parseFloat(value)
                    : 0;
                if (!isNaN(currentPrice)) {
                  const suggestedPrice = currentPrice + 0.01;
                  errorMessage = `Bid Price doesn't follow VA Validation rule. Please use ${suggestedPrice.toFixed(2)}`;
                  return errorMessage;
                }
              }
              return null;
            }
          }
          return null;
        }
      }
      return null;
    }

    const isDirect = currentProductType === 'direct';
    const schema = isDirect
      ? createDirectProductSchema(analysisType)
      : createIndirectProductSchema(analysisType);
    const fieldSchema = schema.shape[fieldName as keyof typeof schema.shape];

    if (fieldSchema) {
      try {
        fieldSchema.parse(value);
        return null;
      } catch (error: any) {
        if (fieldName === 'vaAnnualUsage' || fieldName === 'dodAnnualUsage') {
          const numValue =
            value !== null && value !== undefined
              ? typeof value === 'number'
                ? value
                : parseFloat(String(value))
              : null;
          if (numValue !== null && !isNaN(numValue)) {
            return null;
          }
        }
        let issues = error?.issues;
        if (!issues && error?.errors) {
          issues = error.errors;
        }
        if (!issues && Array.isArray(error)) {
          issues = error;
        }
        if (issues && Array.isArray(issues) && issues.length > 0) {
          return issues[0]?.message || 'Invalid value';
        }
        return 'Invalid value';
      }
    }
    if (isNC && !fieldSchema) {
      return null;
    }
    return null;
  } catch (error: any) {
    let issues = error?.issues;
    if (!issues && error?.errors) {
      issues = error.errors;
    }
    if (!issues && Array.isArray(error)) {
      issues = error;
    }

    if (issues && Array.isArray(issues) && issues.length > 0) {
      const firstIssue = issues[0];
      let errorMessage = firstIssue?.message;

      if (errorMessage) {
        if (
          errorMessage === 'BID_PRICE_VA_VALIDATION_FAILED' &&
          fieldName === 'suggestedBidPrice'
        ) {
          const currentPrice =
            value !== null && value !== undefined
              ? typeof value === 'number'
                ? value
                : parseFloat(value)
              : 0;
          if (!isNaN(currentPrice)) {
            const suggestedPrice = currentPrice + 0.01;
            errorMessage = `Bid Price doesn't follow VA Validation rule. Please use ${suggestedPrice.toFixed(2)}`;
          }
        }
        return errorMessage;
      }
    }
    const currentBidDetails = deps.currentBidDetails;
    const currentTemplateType =
      currentBidDetails?.template_type?.toLowerCase() || deps.templateType?.toLowerCase() || 'mas';
    if (currentTemplateType === 'nc') {
      return null;
    }
    return 'Invalid value';
  }
}
